/**
 * Pre-Toss Squad Selection
 * The scorer picks the Playing Squad (size = event.playing_xi) + Captain + Vice-Captain + Wicketkeeper
 * for both teams before the toss can happen.
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';

export default function SquadSelectionPanel({ eventId, fixtureId, onLocked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // working selections keyed by team_id: { [team_id]: Map(player_pool_id -> {is_captain, is_vice_captain, is_wicketkeeper}) }
  const [selections, setSelections] = useState({});

  const fetchSquads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/squads`);
      setData(res.data);
      // Initialise working selections:
      //   • If the server already has a saved squad for this team, use it.
      //   • Otherwise pre-select the full roster (scorer just unchecks anyone
      //     not playing) — faster than ticking 11 boxes from scratch.
      const initial = {};
      const rosterByTeam = res.data.roster_by_team || {};
      (res.data.teams || []).forEach(t => {
        initial[t.team_id] = {};
        const hasSavedSquad = Array.isArray(t.players) && t.players.length > 0;
        if (hasSavedSquad) {
          t.players.forEach(p => {
            initial[t.team_id][p.player_pool_id] = {
              is_captain: !!p.is_captain,
              is_vice_captain: !!p.is_vice_captain,
              is_wicketkeeper: !!p.is_wicketkeeper,
            };
          });
        } else {
          // Fresh selection — pre-tick every rostered player and auto-mark
          // the first player as Captain. Backend requires exactly one Captain
          // for save+lock; without a default the user has to remember to
          // assign one or the lock silently rejects.
          (rosterByTeam[t.team_id] || []).forEach((p, idx) => {
            initial[t.team_id][p.player_pool_id] = {
              is_captain: idx === 0,
              is_vice_captain: false,
              is_wicketkeeper: false,
            };
          });
        }
      });
      setSelections(initial);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load squads');
    } finally {
      setLoading(false);
    }
  }, [eventId, fixtureId]);

  useEffect(() => { fetchSquads(); }, [fetchSquads]);

  const togglePlayer = (teamId, playerId) => {
    setSelections(prev => {
      const teamSel = { ...(prev[teamId] || {}) };
      if (teamSel[playerId]) {
        delete teamSel[playerId];
      } else {
        teamSel[playerId] = { is_captain: false, is_vice_captain: false, is_wicketkeeper: false };
      }
      return { ...prev, [teamId]: teamSel };
    });
  };

  const setRole = (teamId, playerId, roleKey) => {
    // roles are exclusive (one C / one VC / one WK per team),
    // but C and VC can't be the same player; WK can overlap with C or VC.
    setSelections(prev => {
      const teamSel = { ...(prev[teamId] || {}) };
      if (!teamSel[playerId]) return prev; // must be selected first
      const currentlyOn = !!teamSel[playerId][roleKey];
      // If turning on C -> remove C flag from every other player, and if this player was VC, clear VC.
      // If turning on VC -> remove VC flag from every other player, and if this player was C, clear C.
      // If turning on WK -> remove WK flag from every other player (may coexist with C/VC).
      Object.keys(teamSel).forEach(pid => {
        if (pid === playerId) return;
        if (currentlyOn) return; // we are turning off; don't touch others
        teamSel[pid] = { ...teamSel[pid], [roleKey]: false };
      });
      const cur = teamSel[playerId];
      const nextVal = !currentlyOn;
      let updated = { ...cur, [roleKey]: nextVal };
      if (nextVal) {
        if (roleKey === 'is_captain') updated.is_vice_captain = false;
        if (roleKey === 'is_vice_captain') updated.is_captain = false;
      }
      teamSel[playerId] = updated;
      return { ...prev, [teamId]: teamSel };
    });
  };

  const submitAndLock = async (teamId) => {
    const required = data?.required_size || 11;
    const minSize = data?.min_size || required;
    const team = (data?.teams || []).find(t => t.team_id === teamId);
    if (!team) return;
    const sel = selections[teamId] || {};
    const picked = Object.entries(sel).map(([player_pool_id, flags]) => ({
      player_pool_id,
      is_captain: !!flags.is_captain,
      is_vice_captain: !!flags.is_vice_captain,
      is_wicketkeeper: !!flags.is_wicketkeeper,
    }));

    if (picked.length < minSize || picked.length > required) {
      const msg = minSize === required
        ? `Select exactly ${required} players (currently ${picked.length})`
        : `Select between ${minSize} and ${required} players (currently ${picked.length})`;
      toast.error(msg);
      return;
    }
    if (picked.filter(p => p.is_captain).length !== 1) {
      toast.error('Select exactly one Captain');
      return;
    }
    if (picked.filter(p => p.is_wicketkeeper).length > 1) {
      toast.error('At most one Wicketkeeper allowed');
      return;
    }
    if (picked.filter(p => p.is_vice_captain).length > 1) {
      toast.error('At most one Vice-Captain allowed');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put(
        `/v1/events/${eventId}/live/${fixtureId}/squads/${teamId}`,
        { players: picked }
      );
      await axiosInstance.post(
        `/v1/events/${eventId}/live/${fixtureId}/squads/${teamId}/lock`
      );
      toast.success(`${team.team_name} squad locked`);
      await fetchSquads();
      if (onLocked) onLocked();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save squad');
    } finally {
      setSaving(false);
    }
  };

  const unlock = async (teamId) => {
    if (!window.confirm('Unlock this squad for editing?')) return;
    setSaving(true);
    try {
      await axiosInstance.delete(`/v1/events/${eventId}/live/${fixtureId}/squads/${teamId}/lock`);
      toast.success('Squad unlocked');
      await fetchSquads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }
  if (!data) return null;

  const required = data.required_size;
  const minSize = data.min_size || required;
  const sizeLabel = minSize === required ? `${required}` : `${minSize}–${required}`;

  return (
    <div className="space-y-6" data-testid="squad-selection-panel">
      <div className="bg-[#2B3A67] rounded-xl p-4 text-center">
        <h3 className="text-white font-bold text-lg">
          <i className="ph-bold ph-users-three mr-2"></i>
          Pre-Toss Squad Selection
        </h3>
        <p className="text-white/70 text-sm mt-1">
          Pick {sizeLabel} players per team. Assign a Captain. Vice-Captain & Wicketkeeper are optional.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data.teams || []).map(team => {
          const roster = data.roster_by_team?.[team.team_id] || [];
          const sel = selections[team.team_id] || {};
          const pickedCount = Object.keys(sel).length;
          const hasC = Object.values(sel).some(v => v.is_captain);
          const hasVC = Object.values(sel).some(v => v.is_vice_captain);
          const hasWK = Object.values(sel).some(v => v.is_wicketkeeper);
          const locked = team.is_locked;

          return (
            <div
              key={team.team_id}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
              data-testid={`squad-team-${team.team_id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {team.team_logo_url && (
                    <img src={team.team_logo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  )}
                  <h4 className="font-bold text-[#2B3A67]">{team.team_name}</h4>
                </div>
                <div
                  className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    locked ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}
                  data-testid={`squad-lock-status-${team.team_id}`}
                >
                  {locked ? 'LOCKED' : `${pickedCount}/${sizeLabel}`}
                </div>
              </div>

              {roster.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-4 text-center">
                  No players assigned to this team yet.
                </div>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
                  {roster.map(p => {
                    const picked = !!sel[p.player_pool_id];
                    const flags = sel[p.player_pool_id] || {};
                    const disabled = locked || saving;
                    return (
                      <div
                        key={p.player_pool_id}
                        className={`flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm ${
                          picked ? 'bg-[#3AAFA9]/10 border border-[#3AAFA9]/30' : 'bg-gray-50 border border-transparent'
                        }`}
                        data-testid={`roster-row-${p.player_pool_id}`}
                      >
                        <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={picked}
                            disabled={disabled || (!picked && pickedCount >= required)}
                            onChange={() => togglePlayer(team.team_id, p.player_pool_id)}
                            data-testid={`squad-pick-${p.player_pool_id}`}
                          />
                          {p.player_photo ? (
                            <img src={p.player_photo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">
                              {p.player_name?.[0] || '?'}
                            </span>
                          )}
                          <span className="truncate font-medium text-gray-800">{p.player_name}</span>
                          {p.playing_role && (
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">
                              {p.playing_role}
                            </span>
                          )}
                        </label>
                        {picked && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => setRole(team.team_id, p.player_pool_id, 'is_captain')}
                              disabled={disabled}
                              title="Captain"
                              className={`text-[10px] font-bold w-6 h-6 rounded-full ${
                                flags.is_captain ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 border border-amber-300'
                              }`}
                              data-testid={`role-c-${p.player_pool_id}`}
                            >C</button>
                            <button
                              onClick={() => setRole(team.team_id, p.player_pool_id, 'is_vice_captain')}
                              disabled={disabled}
                              title="Vice-Captain"
                              className={`text-[10px] font-bold w-6 h-6 rounded-full ${
                                flags.is_vice_captain ? 'bg-indigo-500 text-white' : 'bg-white text-indigo-700 border border-indigo-300'
                              }`}
                              data-testid={`role-vc-${p.player_pool_id}`}
                            >VC</button>
                            <button
                              onClick={() => setRole(team.team_id, p.player_pool_id, 'is_wicketkeeper')}
                              disabled={disabled}
                              title="Wicketkeeper"
                              className={`text-[10px] font-bold w-6 h-6 rounded-full ${
                                flags.is_wicketkeeper ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 border border-emerald-300'
                              }`}
                              data-testid={`role-wk-${p.player_pool_id}`}
                            >WK</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-3 text-[11px] text-gray-500">
                C: {hasC ? '✓' : '—'} &nbsp; VC: {hasVC ? '✓' : 'opt'} &nbsp; WK: {hasWK ? '✓' : 'opt'}
              </div>

              <div className="mt-3 flex gap-2">
                {locked ? (
                  <button
                    onClick={() => unlock(team.team_id)}
                    disabled={saving}
                    className="flex-1 py-2 bg-gray-100 text-gray-700 font-medium text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    data-testid={`squad-unlock-${team.team_id}`}
                  >
                    <i className="ph-bold ph-lock-open mr-1"></i>Unlock to edit
                  </button>
                ) : (
                  <button
                    onClick={() => submitAndLock(team.team_id)}
                    disabled={saving || pickedCount < minSize || pickedCount > required || !hasC}
                    className="flex-1 py-2 bg-[#3AAFA9] text-white font-bold text-sm rounded-lg hover:bg-[#2D8A85] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`squad-lock-${team.team_id}`}
                  >
                    {saving ? 'Saving...' : 'Save & Lock Squad'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {data.both_locked ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center" data-testid="both-squads-locked">
          <i className="ph-bold ph-check-circle text-emerald-600 mr-2"></i>
          <span className="text-emerald-800 text-sm font-medium">
            Both squads locked. Proceed to toss below.
          </span>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
          <i className="ph-bold ph-info text-amber-600 mr-2"></i>
          <span className="text-amber-800 text-sm">
            Lock both squads to enable the toss.
          </span>
        </div>
      )}
    </div>
  );
}
